import React, { useEffect, useState } from "react";
import { listActions } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import DetailDrawer from "@/components/DetailDrawer";
import ManualActionDialog from "@/components/ManualActionDialog";

const FILTERS = [
    { key: "", label: "ALL" },
    { key: "success", label: "SUCCESS" },
    { key: "failed", label: "FAILED" },
    { key: "pending", label: "PENDING" },
    { key: "needs_review", label: "NEEDS REVIEW" },
];

// v2 — receipt-type filters. Frontend-only. Map each chip to one or more
// `action.type` values produced by the backend so existing log data is
// reused as-is. Adding new types later is a one-line change.
const TYPE_FILTERS = [
    { key: "all", label: "All", types: null },
    { key: "find_prospects", label: "Find Prospects", types: ["find_prospects"] },
    { key: "score", label: "Score", types: ["score_prospect"] },
    { key: "drafts", label: "Drafts", types: ["draft_outreach"] },
    { key: "blocked", label: "Blocked", types: ["draft_blocked"] },
];

export default function ActionLog() {
    const [filter, setFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const rows = await listActions(filter || undefined);
        setItems(rows);
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const activeTypeSet = (() => {
        const f = TYPE_FILTERS.find((x) => x.key === typeFilter);
        return f && f.types ? new Set(f.types) : null;
    })();

    const filtered = items.filter((a) => {
        if (activeTypeSet && !activeTypeSet.has(a.type)) return false;
        if (!q) return true;
        const s = q.toLowerCase();
        return (
            a.target?.toLowerCase().includes(s) ||
            a.type?.toLowerCase().includes(s) ||
            a.id?.toLowerCase().includes(s)
        );
    });

    return (
        <div data-testid="log-page">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                    // ledger
                </div>
                <h1 className="text-4xl font-bold tracking-tight">Action Log</h1>
                <p className="font-mono text-[11px] text-[#9ca3af] mt-1">
                    Every action. Every attempt. Every receipt.
                </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
                {FILTERS.map((f) => (
                    <button
                        key={f.key || "all"}
                        onClick={() => setFilter(f.key)}
                        data-testid={`filter-${f.key || "all"}`}
                        className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-[2px] border transition-colors ${
                            filter === f.key
                                ? "border-[#7cc93c] text-[#7cc93c] bg-[#7cc93c]/5"
                                : "border-[#262a2e] text-[#9ca3af] hover:border-[#3a3f44] hover:text-[#f3f4f6]"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="search target, type, id…"
                    data-testid="log-search"
                    className="ml-auto w-72 bg-[#111315] border border-[#262a2e] font-mono text-[11px] px-3 py-1.5 rounded-[2px] focus:border-[#7cc93c] outline-none"
                />
                <ManualActionDialog onCreated={load} />
            </div>

            {/* v2 — receipt-type filters (frontend-only) */}
            <div
                className="flex flex-wrap items-center gap-2 mb-4"
                data-testid="log-type-filters"
            >
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#6b7280] mr-1">
                    receipt type:
                </span>
                {TYPE_FILTERS.map((tf) => {
                    const count = tf.types
                        ? items.filter((a) => tf.types.includes(a.type)).length
                        : items.length;
                    const active = typeFilter === tf.key;
                    return (
                        <button
                            key={tf.key}
                            onClick={() => setTypeFilter(tf.key)}
                            data-testid={`type-filter-${tf.key}`}
                            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-[2px] border transition-colors flex items-center gap-1.5 ${
                                active
                                    ? "border-[#7cc93c] text-[#7cc93c] bg-[#7cc93c]/5"
                                    : "border-[#262a2e] text-[#9ca3af] hover:border-[#3a3f44] hover:text-[#f3f4f6]"
                            }`}
                        >
                            {tf.label}
                            <span
                                className={`text-[9px] px-1 rounded-[2px] ${
                                    active
                                        ? "bg-[#7cc93c]/15 text-[#7cc93c]"
                                        : "bg-[#1a1c1f] text-[#9ca3af]"
                                }`}
                            >
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="border border-[#262a2e] bg-[#111315]">
                <div className="grid grid-cols-[110px_140px_1fr_90px_60px_140px] px-5 py-2 border-b border-[#262a2e] font-mono text-[9px] uppercase tracking-[0.25em] text-[#9ca3af]">
                    <div>timestamp</div>
                    <div>type</div>
                    <div>target</div>
                    <div>provider</div>
                    <div className="text-right">retry</div>
                    <div className="text-right">status</div>
                </div>

                {filtered.length === 0 && (
                    <div className="p-12 text-center font-mono text-[11px] text-[#9ca3af] border-b border-[#262a2e]">
                        NO_DATA_FOUND
                    </div>
                )}

                {filtered.map((a) => (
                    <div
                        key={a.id}
                        onClick={() => {
                            setSelected(a);
                            setOpen(true);
                        }}
                        data-testid={`log-row-${a.id}`}
                        className="pc-row border-b last:border-b-0 border-[#262a2e] cursor-pointer"
                    >
                        <div className="grid grid-cols-[110px_140px_1fr_90px_60px_140px] items-center px-5 py-3 font-mono text-[11px]">
                            <div className="text-[#9ca3af]">{a.created_at?.slice(11, 19)}Z</div>
                            <div className="text-[#9ca3af] truncate">{a.type}</div>
                            <div className="truncate text-[#f3f4f6]">{a.target || "—"}</div>
                            <div className="text-[#9ca3af] truncate">{a.provider || "—"}</div>
                            <div className="text-right text-[#9ca3af]">{a.retry_count}</div>
                            <div className="flex justify-end">
                                <StatusPill status={a.status} />
                            </div>
                        </div>
                        {a.status === "failed" && a.error && (
                            <div
                                data-testid={`log-error-${a.id}`}
                                className="px-5 pb-2 -mt-1 font-mono text-[10px] text-[#ff453a] truncate"
                            >
                                ↳ {a.error}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <DetailDrawer action={selected} open={open} onOpenChange={setOpen} />
        </div>
    );
}
